import { useState } from 'react';
import { Box, Button, Typography, CircularProgress } from '@mui/material';
import { VisibilityOutlined as VisibilityIcon } from '@mui/icons-material';
import { useAuth } from '../../hooks/useAuth';

/**
 * Persistent "Viewing as … — Exit" banner shown for the whole duration of an impersonation session.
 *
 * Mounted ABOVE `AppLayout` (in `App.tsx`, inside `AuthProvider`) — NOT inside the routed layout — so it
 * can NEVER be structurally suppressed by AppLayout's early `<Navigate>` portal-confinement return for an
 * impersonated External/Partner target (review H3). Fixed to the viewport bottom (out of the way of the
 * app's top AppBar), full-width, high z-index, in the DRIS warning/orange palette token so it reads as
 * unmistakably different from the green brand chrome and from red error states.
 *
 * The banner is UX only — the SERVER write-guard is the real read-only enforcement, so a spoofed/absent
 * banner cannot grant writes. What matters here is that it is never SILENTLY absent while impersonating.
 */
export const ImpersonationBanner: React.FC = () => {
  const { impersonating, user, impersonator, stopImpersonation } = useAuth();
  const [exiting, setExiting] = useState(false);

  if (!impersonating || !user) {
    return null;
  }

  const handleExit = async () => {
    setExiting(true);
    try {
      await stopImpersonation();
    } finally {
      setExiting(false);
    }
  };

  const targetLabel = user.name?.trim() ? `${user.name} (${user.email})` : user.email;

  return (
    <Box
      role="alert"
      aria-live="polite"
      sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: (theme) => theme.zIndex.snackbar + 1,
        bgcolor: 'warning.dark',
        color: '#ffffff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexWrap: 'wrap',
        gap: 1.5,
        py: 1,
        px: 2,
        boxShadow: 6,
      }}
    >
      <VisibilityIcon fontSize="small" />
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        Viewing as {targetLabel}
        {impersonator?.email ? ` — impersonated by ${impersonator.email}` : ''}
      </Typography>
      <Button
        size="small"
        variant="outlined"
        onClick={handleExit}
        disabled={exiting}
        startIcon={exiting ? <CircularProgress size={14} color="inherit" /> : undefined}
        sx={{ color: '#ffffff', borderColor: '#ffffff', '&:hover': { borderColor: '#ffffff', bgcolor: 'rgba(255,255,255,0.12)' } }}
      >
        {exiting ? 'Exiting…' : 'Exit'}
      </Button>
    </Box>
  );
};

export default ImpersonationBanner;
